import React from 'react';
import { Search, X } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { LoadingAnimation } from '../../../components/ui/loading-animation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import type {
  FeedbackReport,
  FeedbackReportMetadata,
  FeedbackReportNote,
  DuplicateOption,
} from './types';
import {
  formatDateTime,
  formatMetadataValue,
  truncateText,
  normalizeSearch,
} from './utils';

export type FeedbackReportDetailProps = {
  detailLoading: boolean;
  selectedReport: FeedbackReport | null;
  selectedReportId: number | null;
  currentUserId: number | null;
  metadata: FeedbackReportMetadata;
  statusLabelMap: Map<string, string>;
  severityLabelMap: Map<string, string>;
  selectedReportMetadataEntries: Array<[string, unknown]>;
  formStatus: string;
  onFormStatusChange: (value: string) => void;
  formClosedReason: string | null;
  onFormClosedReasonChange: (value: string | null) => void;
  formSeverity: string;
  onFormSeverityChange: (value: string) => void;
  formDuplicate: DuplicateOption | null;
  onClearDuplicate: () => void;
  requiresClosedReason: boolean;
  requiresSeverity: boolean;
  isDuplicateRequired: boolean;
  formError: string | null;
  onSave: () => void;
  onReset: () => void;
  isSaveDisabled: boolean;
  saving: boolean;
  editingFeedback: boolean;
  feedbackDraft: string;
  onFeedbackDraftChange: (value: string) => void;
  feedbackError: string | null;
  onStartEditingFeedback: () => void;
  onCancelEditingFeedback: () => void;
  onSaveFeedback: () => void;
  feedbackSaving: boolean;
  isFeedbackSaveDisabled: boolean;
  duplicatePickerOpen: boolean;
  onToggleDuplicatePicker: (open: boolean) => void;
  duplicateSearch: string;
  onDuplicateSearchChange: (value: string) => void;
  duplicateLoading: boolean;
  duplicateResults: FeedbackReport[];
  onSelectDuplicate: (report: FeedbackReport) => void;
  notes: FeedbackReportNote[];
  notesLoading: boolean;
  noteDraft: string;
  onNoteDraftChange: (value: string) => void;
  noteSaving: boolean;
  onAddNote: () => void;
  editingNoteId: number | null;
  editingNoteValue: string;
  onEditingNoteValueChange: (value: string) => void;
  editingNoteSaving: boolean;
  editingNoteError: string | null;
  onStartEditingNote: (note: FeedbackReportNote) => void;
  onCancelEditingNote: () => void;
  onSaveEditedNote: () => void;
};

export const FeedbackReportDetail: React.FC<FeedbackReportDetailProps> = ({
  detailLoading,
  selectedReport,
  selectedReportId,
  currentUserId,
  metadata,
  statusLabelMap,
  severityLabelMap,
  selectedReportMetadataEntries,
  formStatus,
  onFormStatusChange,
  formClosedReason,
  onFormClosedReasonChange,
  formSeverity,
  onFormSeverityChange,
  formDuplicate,
  onClearDuplicate,
  requiresClosedReason,
  requiresSeverity,
  isDuplicateRequired,
  formError,
  onSave,
  onReset,
  isSaveDisabled,
  saving,
  editingFeedback,
  feedbackDraft,
  onFeedbackDraftChange,
  feedbackError,
  onStartEditingFeedback,
  onCancelEditingFeedback,
  onSaveFeedback,
  feedbackSaving,
  isFeedbackSaveDisabled,
  duplicatePickerOpen,
  onToggleDuplicatePicker,
  duplicateSearch,
  onDuplicateSearchChange,
  duplicateLoading,
  duplicateResults,
  onSelectDuplicate,
  notes,
  notesLoading,
  noteDraft,
  onNoteDraftChange,
  noteSaving,
  onAddNote,
  editingNoteId,
  editingNoteValue,
  onEditingNoteValueChange,
  editingNoteSaving,
  editingNoteError,
  onStartEditingNote,
  onCancelEditingNote,
  onSaveEditedNote,
}) => {
  return (
    <Card maxWidth="none">
      <CardHeader>
        <CardTitle title="Report details" />
      </CardHeader>
      <CardContent variant="custom" className="flex flex-col gap-4">
        {detailLoading && !selectedReport ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingAnimation />
          </div>
        ) : selectedReport ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Report #{selectedReport.id}</h2>
                <p className="text-sm text-muted-foreground">
                  Created {formatDateTime(selectedReport.created_at)} by{' '}
                  {selectedReport.first_name || selectedReport.last_name
                    ? `${selectedReport.first_name ?? ''} ${selectedReport.last_name ?? ''}`.trim()
                    : selectedReport.username ?? 'Unknown'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="default">
                  {statusLabelMap.get(selectedReport.status) ??
                    selectedReport.status_display ??
                    selectedReport.status}
                </Badge>
                {selectedReport.severity ? (
                  <Badge variant="secondary">
                    {severityLabelMap.get(selectedReport.severity) ??
                      selectedReport.severity_display ??
                      selectedReport.severity}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-white/40 bg-white/20 p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Feedback
                  </span>
                  {currentUserId === selectedReport.user && !editingFeedback ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="auto"
                      className="h-8 px-3 text-xs"
                      onClick={onStartEditingFeedback}
                      disabled={feedbackSaving || saving}
                    >
                      Edit feedback
                    </Button>
                  ) : null}
                </div>
                {editingFeedback ? (
                  <>
                    <Textarea
                      value={feedbackDraft}
                      onChange={(event) => onFeedbackDraftChange(event.target.value)}
                      rows={5}
                      className="mt-3"
                    />
                    {feedbackError ? (
                      <div className="mt-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                        {feedbackError}
                      </div>
                    ) : null}
                    <div className="mt-3 flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="auto"
                        onClick={onCancelEditingFeedback}
                        disabled={feedbackSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="auto"
                        onClick={onSaveFeedback}
                        disabled={isFeedbackSaveDisabled}
                      >
                        {feedbackSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                    {selectedReport.feedback_text}
                  </p>
                )}
              </div>
              <div className="rounded-lg border border-white/40 bg-white/20 p-3 text-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Metadata
                </div>
                {selectedReportMetadataEntries.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {selectedReportMetadataEntries.map(([key, value]) => (
                      <div key={key}>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {key}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatMetadataValue(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">No metadata provided.</p>
                )}
              </div>
            </div>

            <div className="space-y-4 rounded-lg border border-white/40 bg-white/20 p-3 text-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Status
                  </label>
                  <Select value={formStatus} onValueChange={onFormStatusChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                    <SelectContent>
                      {(metadata.status_choices ?? []).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {requiresSeverity ? (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Severity
                    </label>
                    <Select value={formSeverity || undefined} onValueChange={onFormSeverityChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select severity" />
                      </SelectTrigger>
                      <SelectContent>
                        {(metadata.severity_choices ?? []).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                {requiresClosedReason ? (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Closed reason
                    </label>
                    <Select
                      value={formClosedReason ?? undefined}
                      onValueChange={(value) => onFormClosedReasonChange(value || null)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {(metadata.closed_reason_choices ?? []).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>

              {formClosedReason === 'duplicate' ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Duplicate of
                    </label>
                    {formDuplicate ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onClearDuplicate}
                        className="h-8 w-8"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-3 rounded-lg border border-dashed border-border/60 bg-white/5 p-3 text-sm">
                    {formDuplicate ? (
                      <div>
                        <div className="font-medium">
                          Report #{formDuplicate.id}{' '}
                          <span className="text-muted-foreground">
                            ({formDuplicate.status_display ?? formDuplicate.status})
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Created {formatDateTime(formDuplicate.created_at)}
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {truncateText(formDuplicate.feedback_text, 200)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Select the canonical report that this feedback duplicates.
                      </p>
                    )}
                    <Dialog open={duplicatePickerOpen} onOpenChange={onToggleDuplicatePicker}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="secondary" size="auto">
                          {formDuplicate ? 'Change duplicate' : 'Add duplicate'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-4xl">
                        <DialogHeader>
                          <DialogTitle>Select duplicate report</DialogTitle>
                        </DialogHeader>
                        <div className="flex max-h-[70vh] flex-col gap-4 overflow-hidden">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder="Search by ID or feedback text..."
                              value={duplicateSearch}
                              onChange={(event) => onDuplicateSearchChange(event.target.value)}
                              className="pl-9"
                            />
                          </div>
                          <div className="space-y-2 overflow-hidden">
                            {duplicateLoading ? (
                              <div className="flex h-32 items-center justify-center">
                                <LoadingAnimation size="sm" />
                              </div>
                            ) : duplicateResults.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                                No matching reports found.
                              </div>
                            ) : (
                              <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                                {duplicateResults.map((duplicate) => (
                                  <div
                                    key={duplicate.id}
                                    className="flex flex-col gap-2 rounded-xl border border-border/40 bg-white/60 p-3 text-sm text-foreground"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="font-semibold">Report #{duplicate.id}</div>
                                      <Badge variant="outline">
                                        {statusLabelMap.get(duplicate.status) ??
                                          duplicate.status_display ??
                                          duplicate.status}
                                      </Badge>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {formatDateTime(duplicate.created_at)}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {truncateText(duplicate.feedback_text, 200)}
                                    </p>
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      onClick={() => onSelectDuplicate(duplicate)}
                                    >
                                      Select
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {isDuplicateRequired && !formDuplicate ? (
                    <div className="text-xs text-destructive">
                      Select a duplicate report before saving.
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {formError ? (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {formError}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={onSave} disabled={isSaveDisabled} size="auto">
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
              <Button type="button" variant="secondary" size="auto" onClick={onReset}>
                Reset
              </Button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Notes
                </h3>
                <span className="text-xs text-muted-foreground">
                  {notes.length} note{notes.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="flex flex-col gap-3 rounded-lg border border-white/40 bg-white/20 p-3">
                <Textarea
                  placeholder="Add a triage note..."
                  value={noteDraft}
                  onChange={(event) => onNoteDraftChange(event.target.value)}
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="auto"
                    variant="secondary"
                    onClick={onAddNote}
                    disabled={noteSaving || normalizeSearch(noteDraft).length === 0}
                  >
                    Add note
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {notesLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingAnimation size="sm" />
                  </div>
                ) : notes.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                    No notes yet.
                  </div>
                ) : (
                  notes.map((note) => {
                    const canEdit = currentUserId === note.author;
                    const isEditing = editingNoteId === note.id;
                    const isAnotherNoteEditing = editingNoteId !== null && editingNoteId !== note.id;

                    return (
                      <div
                        key={note.id}
                        className="rounded-xl border border-border/40 bg-white/60 p-3 text-sm text-foreground"
                      >
                        <div className="flex items-start justify-between text-xs text-muted-foreground">
                          <span>
                            {note.author_first_name || note.author_last_name
                              ? `${note.author_first_name ?? ''} ${note.author_last_name ?? ''}`.trim()
                              : note.author_username ?? 'Unknown user'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span>{formatDateTime(note.created_at)}</span>
                            {canEdit && !isEditing ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="auto"
                                className="h-8 px-3 text-xs"
                                onClick={() => onStartEditingNote(note)}
                                disabled={isAnotherNoteEditing || editingNoteSaving}
                              >
                                Edit
                              </Button>
                            ) : null}
                          </div>
                        </div>
                        {isEditing ? (
                          <>
                            <Textarea
                              value={editingNoteValue}
                              onChange={(event) => onEditingNoteValueChange(event.target.value)}
                              rows={3}
                              className="mt-3"
                            />
                            {editingNoteError && editingNoteId === note.id ? (
                              <div className="mt-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                                {editingNoteError}
                              </div>
                            ) : null}
                            <div className="mt-3 flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="secondary"
                                size="auto"
                                onClick={onCancelEditingNote}
                                disabled={editingNoteSaving}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                size="auto"
                                onClick={onSaveEditedNote}
                                disabled={editingNoteSaving}
                              >
                                {editingNoteSaving ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{note.note}</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
            <Search className="h-6 w-6" />
            Select a report to review its details.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
